import type { FC } from 'react'
import { memo, useContext, useEffect, useState } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import styled, { css } from 'styled-components'
import { Config } from '@/core/config'
import { validFor } from '@/theme/validFor'
import { FocusMode, useCurrentPage } from '@/providers/focusModeProvider'
import {
  isOrderPage,
  useOrderDispatch
} from '@/providers/order/old/orderDispatchProvider'
import type {
  GetClientDataQuery,
  HeaderNotice,
  Market,
  Sdk
} from '@/network/graphql.g'
import { DeviceType, MarketCode, NavigationItem } from '@/network/graphql.g'
import { Routes } from '@/core/routes'
import useSWR from 'swr'
import { useUser } from '@/providers/userProvider'
import { useRouter } from 'next/router'
import useGraphQl from 'hooks/useGraphQl'
import { getSessionId } from '@/utils/sessionService'
import { getUtmSource } from '@/utils/utmSourceService'
import { useWishlist } from '@/providers/wishlistProvider'
import { getLocalWishlistIds } from '@/providers/wishlistDispatchProvider'
import { getShallowParams } from '@/utils/routerHelpers'
import { useOrder } from '@/providers/order/old/orderProvider'
import { useMhub } from '@/providers/mhubProvider'
import { LoginType } from '@/providers/mhub/userManager'
import dynamic from 'next/dynamic'
import objectHash from '@/core/crypto/objectHash'
import useDevice from 'hooks/useDevice'
import { useMainNavigationDispatch } from '@/providers/mainNavigationMenuProvider'
import { useHeaderNoticeDispatch } from '@/providers/headerNoticeProvider'
import { useCookie } from 'next-cookie'
import { StoreContext, useDomain } from '@/providers/storeProvider'
import { useAsset } from '@/providers/assetsProvider'
import {
  ExperimentType,
  InfoPromoPopUpDisplayVariant,
  useVariant
} from '@/providers/experimentProvider'
import IconsDefinitionListFrogies from '@/components/atoms/svg/iconsDefinitions/IconsDefinitionListFrogies'
import IconsDefinitionList from '@/components/atoms/svg/iconsDefinitions/IconsDefinitionList'

const DynamicHeaderRedesign = dynamic(() => import('./headerRedesign'))

const DynamicFooter = dynamic(() => import('@/components/organisms/footer'), {
  ssr: false
})
const DynamicHeaderPromo = dynamic(
  () => import('@/components/molecules/headerPromo/headerPromo')
)

const DynamicInfoPromoPopUp = dynamic(
  () => import('@/components/organisms/infoPromoPopup'),
  {
    ssr: false,
    loading: () => null
  }
)

type ContainerProps = {
  facebookRegistration: boolean
  headerNotice?: HeaderNotice
}

const Container: FC<ContainerProps> = memo(
  ({ facebookRegistration, headerNotice: serverHeaderNotice, children }) => {
    const { reloadOrder, abortOrderReload } = useOrderDispatch()
    const { getOrderChecksum, createOrderChecksum } = useOrder()
    const {
      isLoggedInBySession,
      updateUser,
      user,
      getUserChecksum,
      isLoggedIn
    } = useUser()
    const router = useRouter()
    const { pushLoginEvent, pushRegisterEvent, pushCustomerFromMailing } =
      useMhub()
    const { isFrogies } = useContext(StoreContext)
    const { setMainNavigation } = useMainNavigationDispatch()
    const { setHeaderNotice: setHeaderNoticeContext } =
      useHeaderNoticeDispatch()
    const [markets, setMarkets] = useState<Market[]>([])
    const [headerNotice, setHeaderNotice] =
      useState<HeaderNotice>(serverHeaderNotice)
    const { request } = useGraphQl()
    const { setWishlistProductCount } = useWishlist()
    const { getWishlistChecksum, createWishlistChecksum, setWishlist } =
      useWishlist()
    const { isTablet, isDesktop } = useDevice()
    const [showHeaderPromo, setShowHeaderPromo] = useState<boolean>(true)
    const cookie = useCookie()
    const cookieShowInfoPromoPoUpExists = cookie.get('infoPromoPopUp')
    const [showInfoPromoPopUp, setShowInfoPromoPopUp] = useState<boolean>(false)

    useEffect(() => {
      if (cookieShowInfoPromoPoUpExists) {
        return
      }

      const interactionEvents = ['pointerdown', 'keydown', 'scroll']
      let isDelayOver = false
      let hasInteracted = false
      let showTimeoutId: ReturnType<typeof setTimeout>

      const removeInteractionListeners = () =>
        interactionEvents.forEach((event) =>
          window.removeEventListener(event, onInteraction, true)
        )

      const onInteraction = () => {
        hasInteracted = true
        removeInteractionListeners()
        if (isDelayOver) {
          showTimeoutId = setTimeout(() => setShowInfoPromoPopUp(true), 1000)
        }
      }

      interactionEvents.forEach((event) =>
        window.addEventListener(event, onInteraction, {
          capture: true,
          passive: true
        })
      )
      const delayTimeoutId = setTimeout(() => {
        isDelayOver = true
        if (hasInteracted) {
          setShowInfoPromoPopUp(true)
        }
      }, 12000)

      return () => {
        clearTimeout(delayTimeoutId)
        clearTimeout(showTimeoutId)
        removeInteractionListeners()
      }
    }, [cookieShowInfoPromoPoUpExists])

    const deviceType = isDesktop
      ? DeviceType.Desktop
      : isTablet
      ? DeviceType.Tablet
      : DeviceType.Mobile

    const loadMinorData = async () => {
      const ssrUrlTopMenu = router.asPath.replace(/\d+/g, '')
      const withMainNavigation = ssrUrlTopMenu === '/product/'
      const wishlistChanged = createWishlistChecksum() !== getWishlistChecksum()
      const customerChanged = objectHash(user ?? null) !== getUserChecksum()
      const { success, response } = await request(
        (sdk: Sdk) => ({
          method: sdk.getMinorData,
          variables: {
            withMainNavigation,
            withMarkets: true,
            withCustomerOrLoggedInWishlist:
              customerChanged || (wishlistChanged && isLoggedInBySession()),
            withLoggedInWishlist: wishlistChanged && isLoggedInBySession(),
            device: deviceType,
            ids: getLocalWishlistIds() ?? [],
            withLoggedOutWishlist: wishlistChanged && !isLoggedInBySession()
          },
          requestHeaders: isLoggedIn
            ? {}
            : {
                'x-cachable': '1'
              }
        }),
        true
      )
      if (success) {
        if (withMainNavigation) {
          setMainNavigation(
            JSON.parse(response?.mainNavigationJson) as NavigationItem
          )
        }
        setMarkets(response?.markets)
      }
      if (success) {
        setHeaderNotice(response?.headerNotices)
        setHeaderNoticeContext(response?.headerNotices)
        if (wishlistChanged) {
          setWishlist(
            isLoggedInBySession()
              ? response?.activeCustomer?.wishlistProducts
              : response?.loggedOutWishlist
          )
          setWishlistProductCount(
            isLoggedInBySession() &&
              response?.activeCustomer?.wishlistProductsCount
          )
        }
      }
    }

    const getCustomerFromMailingData = async (mailingId: string) => {
      const { response } = await request(
        (sdk: Sdk) => ({
          method: sdk.getCustomerFromMailingData,
          variables: {
            mailingId
          }
        }),
        true
      )
      return response
    }

    const isCustomerFromMail =
      router.query?.utm_term &&
      router.query?.utm_medium === 'email' &&
      !isLoggedIn

    const maybeOptionsKey = isCustomerFromMail ? router.query?.utm_term : null

    const { data: customerFromMailing } = useSWR(
      maybeOptionsKey,
      getCustomerFromMailingData,
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: true
      }
    )

    useEffect(() => {
      if (customerFromMailing) {
        pushCustomerFromMailing(
          LoginType.Newsletter,
          customerFromMailing.getCustomerFromMailingData
        )
      }
    }, [customerFromMailing])

    useEffect(() => {
      facebookRegistration && pushRegisterEvent(LoginType.Facebook)

      if (router.query?.loginSuccess) {
        pushLoginEvent(router.query?.loginSuccess as LoginType)
        delete router.query.loginSuccess
        const { url, as } = getShallowParams(router)
        router.replace(url, as, { shallow: true })
      }
      loadMinorData()
    }, [])

    const { getFocusType } = useCurrentPage()
    const isFullFocus = getFocusType() === FocusMode.Full

    const clientDataFetcher = async (
      _key,
      _isLoggedIn,
      path
    ): Promise<GetClientDataQuery> => {
      const customerChanged =
        objectHash(user ?? null) !== getUserChecksum() || isLoggedInBySession()
      const cartChanged = createOrderChecksum() !== getOrderChecksum()
      const wishlistChanged = createWishlistChecksum() !== getWishlistChecksum()
      let result: GetClientDataQuery

      if (cartChanged || customerChanged) {
        const abortSignal = abortOrderReload()
        const orderPage = isOrderPage(path)
        const { success, response } = await request(
          (sdk: Sdk) => ({
            method: sdk.getClientData,
            variables: {
              withCart: cartChanged,
              withCustomer: customerChanged,
              withLoggedInWishlist: wishlistChanged && isLoggedInBySession(),
              withLoggedOutWishlist: wishlistChanged && !isLoggedInBySession(),
              withCustomerOrLoggedInWishlist:
                customerChanged || (wishlistChanged && isLoggedInBySession()),
              sessionId: getSessionId(),
              utmSource: getUtmSource(),
              withMHubOrder: orderPage,
              checkQuantity: orderPage,
              ids: getLocalWishlistIds() ?? [],
              ipAddress: '',
              wasInAdmin: false,
              device: isDesktop
                ? DeviceType.Desktop
                : isTablet
                ? DeviceType.Tablet
                : DeviceType.Mobile
            }
          }),
          true,
          null,
          abortSignal
        )
        if (success) {
          if (customerChanged) {
            updateUser(response?.activeCustomer, false)
          }
          if (cartChanged) {
            reloadOrder(response?.getCart)
          }
          result = response
        }
      }
      return result
    }

    useSWR(
      ['clientData', isLoggedInBySession(), router.pathname],
      clientDataFetcher
    )

    const { isCartProcessStep, isInfoPopUpPromoTime } = useAsset()

    const { subdomainName } = useDomain()
    const { isFactcool, isBezvasport } = useContext(StoreContext)
    const isCountryForInfoPopUp = [MarketCode.Ba, MarketCode.Rs].includes(
      subdomainName as MarketCode
    )

    const [showSlideMenu, setShowSlideMenu] = useState<boolean>(false)

    const infoPromoVariant = useVariant(ExperimentType.InfoPromoPopUpDisplay)
    const isProductDetail = router.pathname.startsWith('/product/')
    const isStandaloneLanding = [
      Routes.suppliersRegistration,
      Routes.vipCouponsPage
    ].includes(router.pathname)
    const hideInfoPromoByExperiment =
      isProductDetail &&
      infoPromoVariant === InfoPromoPopUpDisplayVariant.VariantA

    const shouldShowInfoPromo =
      showInfoPromoPopUp &&
      !isCountryForInfoPopUp &&
      !isStandaloneLanding &&
      isInfoPopUpPromoTime &&
      (isFactcool || isBezvasport) &&
      !hideInfoPromoByExperiment

    const SharedHeaderPromo = ({
      headerNotice,
      showHeaderPromo,
      setShowHeaderPromo
    }: {
      headerNotice: HeaderNotice
      showHeaderPromo: boolean
      setShowHeaderPromo: (value: boolean) => void
    }) => (
      <StyleDynamicHeaderPromo
        data-testid="headerPromo"
        headerNotice={headerNotice}
        showHeaderPromo={showHeaderPromo}
        setShowHeaderPromo={setShowHeaderPromo}
      />
    )

    const renderMainContent = () => (
      <StyledMain>
        {shouldShowInfoPromo && (
          <DynamicInfoPromoPopUp
            setShowInfoPromoPopUp={setShowInfoPromoPopUp}
            showInfoPromoPopUp={showInfoPromoPopUp}
          />
        )}
        {isFrogies ? (
          <IconsDefinitionListFrogies />
        ) : (
          <>
            <IconsDefinitionListFrogies />
            <IconsDefinitionList />
          </>
        )}
        <ContentWrapper>{children}</ContentWrapper>
      </StyledMain>
    )

    const SharedFooter = ({
      isCartProcessStep,
      markets
    }: {
      isCartProcessStep: boolean
      markets: Market[]
    }) => (
      <StyledFooter>
        {!isCartProcessStep && <DynamicFooter markets={markets} />}
      </StyledFooter>
    )

    return (
      <>
        <WrapperRedesign
          flexDirection="column"
          alignItems="stretch"
          suppressHydrationWarning
        >
          {!isStandaloneLanding && (
            <SharedHeaderPromo
              headerNotice={headerNotice}
              showHeaderPromo={showHeaderPromo}
              setShowHeaderPromo={setShowHeaderPromo}
            />
          )}
          <StyledRedesignHeader>
            {!isFullFocus && (
              <DynamicHeaderRedesign
                markets={markets}
                data-testid="headerMenu"
                showHeaderPromo={showHeaderPromo}
                headerNotice={headerNotice}
                showSlideMenu={showSlideMenu}
                setShowSlideMenu={setShowSlideMenu}
              />
            )}
          </StyledRedesignHeader>

          {renderMainContent()}
          <SharedFooter
            isCartProcessStep={isCartProcessStep}
            markets={markets}
          />
        </WrapperRedesign>
      </>
    )
  }
)

export default Container

const StyleDynamicHeaderPromo = styled(DynamicHeaderPromo)`
  flex-shrink: 0;
  z-index: 90;
  position: relative;
`

const StyledMain = styled.div`
  flex-shrink: 0;
  flex-grow: 1;
`

const StyledFooter = styled.div`
  flex-shrink: 0;
`

const ContainerCss = css`
  margin: auto;
  ${validFor.wide`
    max-width: ${Config.wideScreenPageWidth}px;
  `}
`

export const ContentWrapper = styled(Box)`
  ${ContainerCss};
`

const WrapperRedesign = styled(Flex)`
  min-height: 100vh;
`

const StyledRedesignHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 90;
  flex-shrink: 0;
`
